import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, useParams, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import parse from "html-react-parser";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  MoreVertical,
  BookOpen,
  Bookmark,
  PlayCircle,
  CheckCircle2,
  Upload,
  AlertCircle,
  X,
  StickyNote,
  Check,
  ClipboardList,
  Users,
  MessageSquare,
  Send,
  Play,
  Clock,
  Sparkles,
  Layers,
  GraduationCap,
  Video,
  FileText,
  Download,
  ExternalLink,
  Link as LinkIcon,
  File
} from "lucide-react";
import Button from "../../../components/common/Button";
import { useCart } from "../../../hooks/useCart";
import { useToast } from "../../../hooks/otherHooks/useToast";
import { authClientWeb } from "react-redux-django-auth/web";
import { useAuthState } from "react-redux-django-auth";
import { TopicAttachment } from "../../../services/curriculumService";
import GitHubAutograderPanel, { CourseGitHubWorkspaceBanner } from "../components/GitHubAutograderPanel";

// --- Project Standard API Client Helper ---
const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not initialized yet
  }
  const token = localStorage.getItem("access");
  const baseURL = process.env.REACT_APP_API_URL || "";
  return axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  });
};

// --- Helper: Extract YouTube ID ---
const getYouTubeID = (url: string) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

// --- Helper: Format Edited Date ---
const formatEditedDate = (dateStr?: string) => {
  if (!dateStr) return "Edited recently";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "Edited recently";
    return `Edited ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  } catch {
    return "Edited recently";
  }
};

// --- Helper: Format Detailed Date ---
const formatDetailedDate = (dateStr?: string) => {
  if (!dateStr) return "Recently posted";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "Recently posted";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "Recently posted";
  }
};

// --- Embedded Video Player ---
const VideoPlayer = ({ url, title }: { url: string; title: string }) => {
  const videoId = useMemo(() => getYouTubeID(url), [url]);

  if (!videoId) {
    return (
      <div className="cht-w-full cht-aspect-video cht-bg-slate-900 cht-flex cht-items-center cht-justify-center cht-rounded-[20px] cht-p-6 cht-text-center">
        <div className="cht-space-y-3">
          <AlertCircle size={36} strokeWidth={1.5} className="cht-text-slate-500 cht-mx-auto" />
          <p className="cht-text-slate-400 cht-text-xs cht-font-medium">Video content unavailable</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cht-w-full cht-aspect-video cht-bg-black cht-rounded-[20px] cht-overflow-hidden cht-shadow-md cht-border cht-border-black/10 cht-relative">
      <iframe
        className="cht-w-full cht-h-full"
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

// --- Exercise Submission View ---
const ExerciseSubmission = ({
  exercise,
  courseId,
  moduleId,
  track,
  courseTemplateRepo,
}: {
  exercise: any;
  courseId?: any;
  moduleId?: any;
  track?: string;
  courseTemplateRepo?: string;
}) => {
  if (exercise?.requires_autograder) {
    return (
      <GitHubAutograderPanel
        exercise={exercise}
        courseId={courseId || exercise?.courseId}
        moduleId={moduleId || exercise?.moduleId}
        track={track || exercise?.track}
        courseTemplateRepo={courseTemplateRepo || exercise?.courseTemplateRepo}
      />
    );
  }

  return (
    <div className="cht-p-5 cht-bg-slate-50 cht-rounded-[20px] cht-border cht-border-slate-100 cht-space-y-4">
      <div className="cht-flex cht-items-center cht-justify-between">
        <div className="cht-flex cht-items-center cht-gap-2">
          <ClipboardList size={18} className="cht-text-[#3e57da]" />
          <span className="cht-text-xs cht-font-bold cht-text-slate-800 cht-uppercase cht-tracking-wider">Exercise Submission</span>
        </div>
        {exercise.exercise_deadline && (
          <span className="cht-text-[11px] cht-font-semibold cht-text-amber-700 cht-bg-amber-50 cht-border cht-border-amber-200/60 cht-px-2.5 cht-py-0.5 cht-rounded-full">
            Due: {exercise.exercise_deadline}
          </span>
        )}
      </div>

      <div className="cht-border-2 cht-border-dashed cht-border-slate-200 cht-rounded-[16px] cht-p-6 cht-text-center cht-space-y-2 hover:cht-border-[#3e57da]/40 hover:cht-bg-[#3e57da]/5 cht-transition-all cht-cursor-pointer">
        <Upload size={22} className="cht-text-slate-400 cht-mx-auto" />
        <p className="cht-text-xs cht-font-semibold cht-text-slate-700">Attach assignment files (PDF, ZIP, DOC)</p>
        <p className="cht-text-[10px] cht-text-slate-400">Drag and drop or click to upload</p>
      </div>

      {exercise.instructor_feedback && (
        <div className="cht-p-3.5 cht-bg-blue-50/50 cht-border cht-border-blue-100 cht-rounded-[14px]">
          <p className="cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-uppercase cht-tracking-widest cht-mb-1">Instructor Feedback</p>
          <p className="cht-text-xs cht-text-slate-600 leading-relaxed">{exercise.instructor_feedback}</p>
        </div>
      )}
    </div>
  );
};

// --- Enhanced Video Attachment Showcase ---
const VideoAttachmentShowcase = ({
  videoUrl,
  title,
  duration,
  onOpenModal,
}: {
  videoUrl: string;
  title: string;
  duration?: string;
  onOpenModal: () => void;
}) => {
  const ytId = useMemo(() => getYouTubeID(videoUrl), [videoUrl]);

  return (
    <div className="cht-pt-2 cht-space-y-3">
      {/* Section Header with Distinct Accent */}
      <div className="cht-flex cht-items-center cht-justify-between">
        <div className="cht-flex cht-items-center cht-gap-2">
          <div className="cht-w-2 cht-h-2 cht-rounded-full cht-bg-rose-500 cht-animate-pulse" />
          <span className="cht-text-xs cht-font-bold cht-tracking-wider cht-uppercase cht-text-slate-800 !cht-font-body">
            Attached Video Lesson
          </span>
          <span className="cht-text-[10px] cht-font-bold cht-bg-rose-50 cht-text-rose-600 cht-border cht-border-rose-200/70 cht-px-2 cht-py-0.5 cht-rounded-full !cht-font-body">
            HD Walkthrough
          </span>
        </div>
        {duration && (
          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[11px] cht-font-semibold cht-text-slate-500 !cht-font-body">
            <Clock size={11} className="cht-text-slate-400" />
            {duration}
          </span>
        )}
      </div>

      {/* Modern High-Impact Video Card - Click to open modal */}
      <div
        onClick={onOpenModal}
        className="cht-w-full sm:cht-max-w-xl cht-rounded-2xl cht-border cht-border-slate-200/90 hover:cht-border-rose-400/80 cht-bg-white cht-overflow-hidden cht-cursor-pointer cht-transition-all cht-group cht-shadow-sm hover:cht-shadow-md"
      >
        <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-stretch">
          {/* Media Thumbnail with Glow & Play Button */}
          <div className="cht-relative cht-w-full sm:cht-w-44 sm:cht-min-w-[176px] cht-h-32 sm:cht-h-auto cht-bg-slate-950 cht-overflow-hidden cht-flex cht-items-center cht-justify-center cht-shrink-0">
            {ytId ? (
              <img
                src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                alt={title}
                className="cht-w-full cht-h-full cht-object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 cht-transition-transform cht-duration-300"
              />
            ) : (
              <div className="cht-w-full cht-h-full cht-bg-gradient-to-br cht-from-slate-900 cht-via-indigo-950 cht-to-slate-900 cht-flex cht-items-center cht-justify-center">
                <Video size={28} className="cht-text-indigo-400/80" />
              </div>
            )}

            {/* Dark Gradient Overlay */}
            <div className="cht-absolute cht-inset-0 cht-bg-gradient-to-t cht-from-black/60 cht-via-black/20 cht-to-transparent" />

            {/* Play Icon Button */}
            <div className="cht-absolute cht-inset-0 cht-flex cht-items-center cht-justify-center">
              <div className="cht-w-10 cht-h-10 cht-rounded-full cht-bg-white/95 group-hover:cht-bg-rose-600 cht-backdrop-blur-sm cht-shadow-lg cht-flex cht-items-center cht-justify-center cht-text-rose-600 group-hover:cht-text-white group-hover:cht-scale-110 cht-transition-all cht-duration-200">
                <Play size={15} className="cht-fill-current cht-ml-0.5" />
              </div>
            </div>

            {/* Duration Chip on Thumbnail */}
            {duration && (
              <div className="cht-absolute cht-bottom-2 cht-right-2 cht-bg-black/85 cht-backdrop-blur-md cht-text-white cht-text-[10px] cht-font-bold cht-px-1.5 cht-py-0.5 cht-rounded-md cht-shadow-sm !cht-font-body">
                {duration}
              </div>
            )}
          </div>

          {/* Content / Details */}
          <div className="cht-p-3.5 sm:cht-p-4 cht-flex cht-flex-col cht-justify-between cht-flex-1 cht-min-w-0 cht-gap-2">
            <div className="cht-space-y-1">
              <div className="cht-flex cht-items-center cht-gap-1.5">
                <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-wider cht-text-rose-600 cht-bg-rose-50 cht-px-2 cht-py-0.5 cht-rounded-md cht-border cht-border-rose-100 !cht-font-body">
                  <Play size={7} className="cht-fill-rose-500" />
                  Video
                </span>
                <span className="cht-text-[11px] cht-font-medium cht-text-slate-400 !cht-font-body">
                  {ytId ? "YouTube Stream" : "Hosted Video"}
                </span>
              </div>

              <h4 className="cht-text-sm cht-font-bold cht-text-slate-900 group-hover:cht-text-[#3e57da] cht-transition-colors cht-line-clamp-2 !cht-font-body cht-leading-snug">
                {title || "Watch Lesson Video"}
              </h4>

              <p className="cht-text-xs cht-text-slate-500 cht-line-clamp-2 !cht-font-body">
                Watch the complete step-by-step walkthrough in high-definition video modal.
              </p>
            </div>

            {/* Action CTA */}
            <div className="cht-pt-2 cht-flex cht-items-center cht-justify-between cht-border-t cht-border-slate-100 cht-mt-1">
              <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-xs cht-font-bold cht-text-[#3e57da] group-hover:cht-underline !cht-font-body">
                <Play size={11} className="cht-fill-[#3e57da]" />
                Watch Video
              </span>
              <span className="cht-text-[11px] cht-text-slate-400 !cht-font-body">
                Click to open video modal
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Google Classroom Attachment Icons for Students ---
const GoogleDriveIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
  </svg>
);

const GoogleDocsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#4285F4"/>
    <path d="M14 2v6h6l-6-6z" fill="#A1C2FA"/>
    <path d="M16 13H8v-1.5h8V13zm0 3H8v-1.5h8V16zm-3 3H8v-1.5h5V19z" fill="#FFFFFF"/>
  </svg>
);

const GoogleSlidesIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z" fill="#F4B400"/>
    <path d="M17 7H7a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1zm-1 8H8V9h8v6z" fill="#FFFFFF"/>
  </svg>
);

const GoogleSheetsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#0F9D58"/>
    <path d="M14 2v6h6l-6-6z" fill="#87CEAC"/>
    <path d="M8 12h8v7H8v-7zm1.5 1.5v1.5h2v-1.5h-2zm3.5 0v1.5h2v-1.5h-2zm-3.5 3v1.5h2v-1.5h-2zm3.5 0v1.5h2v-1.5h-2z" fill="#FFFFFF"/>
  </svg>
);

const GoogleFormsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#7248B9"/>
    <path d="M14 2v6h6l-6-6z" fill="#B39DDB"/>
    <circle cx="9" cy="13" r="1.2" fill="#FFFFFF"/>
    <path d="M12 12.5h4v1h-4v-1zm0 3.5h4v1h-4v-1z" fill="#FFFFFF"/>
    <circle cx="9" cy="17" r="1.2" fill="#FFFFFF"/>
  </svg>
);

const GoogleDrawingsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="4" fill="#DB4437"/>
    <circle cx="8" cy="8" r="3" fill="#FFFFFF"/>
    <rect x="13" y="11" width="6" height="6" fill="#FFFFFF"/>
  </svg>
);

const GoogleVidsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="6" fill="#4285F4"/>
    <path d="M10 8l6 4-6 4V8z" fill="#FFFFFF"/>
  </svg>
);

// --- Topic Attachments & Learning Materials Showcase ---
const TopicAttachmentsSection = ({
  material,
  onOpenVideoModal,
}: {
  material: any;
  onOpenVideoModal: (video: { url: string; title?: string; duration?: string }) => void;
}) => {
  const attachments: TopicAttachment[] = useMemo(() => {
    let list: TopicAttachment[] = Array.isArray(material?.attachments) ? [...material.attachments] : [];
    if (list.length === 0 && material?.video_url) {
      list.push({
        id: "legacy-video",
        type: "youtube",
        title: material.title || "Watch Lesson Video",
        url: material.video_url,
      });
    }
    return list;
  }, [material]);

  const videos = useMemo(
    () => attachments.filter((a) => a.type === "youtube" || a.type === "video"),
    [attachments]
  );
  const files = useMemo(
    () => attachments.filter((a) => a.type === "upload" || a.type === "file"),
    [attachments]
  );
  const links = useMemo(
    () =>
      attachments.filter((a) =>
        ["link", "drive", "doc", "slide", "sheet", "form", "drawing", "vids"].includes(a.type)
      ),
    [attachments]
  );

  if (attachments.length === 0) return null;

  return (
    <div className="cht-space-y-6 cht-pt-2">
      {/* Video Attachments */}
      {videos.map((vid, idx) => (
        <VideoAttachmentShowcase
          key={vid.id || idx}
          videoUrl={vid.url}
          title={vid.title || material?.title || "Lesson Video"}
          duration={material?.duration}
          onOpenModal={() =>
            onOpenVideoModal({
              url: vid.url,
              title: vid.title || material?.title || "Lesson Video",
              duration: material?.duration,
            })
          }
        />
      ))}

      {/* Files & Downloads */}
      {files.length > 0 && (
        <div className="cht-space-y-3">
          <div className="cht-flex cht-items-center cht-gap-2">
            <div className="cht-w-2 cht-h-2 cht-rounded-full cht-bg-amber-500" />
            <span className="cht-text-xs cht-font-bold cht-tracking-wider cht-uppercase cht-text-slate-800 !cht-font-body">
              Attached Learning Files ({files.length})
            </span>
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            {files.map((file, idx) => {
              const ext = (file.file_type || "file").toUpperCase();
              return (
                <div
                  key={file.id || idx}
                  className="cht-p-3.5 cht-rounded-2xl cht-bg-white cht-border cht-border-slate-200/90 hover:cht-border-amber-300 cht-shadow-sm hover:cht-shadow-md cht-flex cht-items-center cht-justify-between cht-gap-3 cht-transition-all"
                >
                  <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                    <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-amber-50 cht-text-amber-700 cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-font-black cht-text-[11px]">
                      {ext.slice(0, 4)}
                    </div>
                    <div className="cht-min-w-0">
                      <h5 className="cht-text-xs cht-font-bold cht-text-slate-800 cht-truncate !cht-font-body">
                        {file.title}
                      </h5>
                      <p className="cht-text-[10px] cht-text-slate-400 !cht-font-body">
                        {ext} {file.file_size ? `• ${file.file_size}` : ""}
                      </p>
                    </div>
                  </div>

                  <a
                    href={file.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-bg-slate-50 hover:cht-bg-[#3e57da] cht-text-slate-700 hover:cht-text-white cht-rounded-xl cht-text-xs cht-font-bold cht-transition-colors cht-shrink-0 !cht-font-body"
                  >
                    <Download size={13} />
                    Download
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* External Links & Cloud Documents */}
      {links.length > 0 && (
        <div className="cht-space-y-3">
          <div className="cht-flex cht-items-center cht-gap-2">
            <div className="cht-w-2 cht-h-2 cht-rounded-full cht-bg-blue-500" />
            <span className="cht-text-xs cht-font-bold cht-tracking-wider cht-uppercase cht-text-slate-800 !cht-font-body">
              Reference Links & Documents ({links.length})
            </span>
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            {links.map((link, idx) => (
              <a
                key={link.id || idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="cht-p-3.5 cht-rounded-2xl cht-bg-white cht-border cht-border-slate-200/90 hover:cht-border-blue-300 cht-shadow-sm hover:cht-shadow-md cht-flex cht-items-center cht-justify-between cht-gap-3 cht-transition-all group"
              >
                <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                  <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-blue-50/70 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                    {link.type === "drive" ? (
                      <GoogleDriveIcon className="cht-w-5 cht-h-5" />
                    ) : link.type === "doc" ? (
                      <GoogleDocsIcon className="cht-w-5 cht-h-5" />
                    ) : link.type === "slide" ? (
                      <GoogleSlidesIcon className="cht-w-5 cht-h-5" />
                    ) : link.type === "sheet" ? (
                      <GoogleSheetsIcon className="cht-w-5 cht-h-5" />
                    ) : link.type === "form" ? (
                      <GoogleFormsIcon className="cht-w-5 cht-h-5" />
                    ) : link.type === "drawing" ? (
                      <GoogleDrawingsIcon className="cht-w-5 cht-h-5" />
                    ) : link.type === "vids" ? (
                      <GoogleVidsIcon className="cht-w-5 cht-h-5" />
                    ) : (
                      <LinkIcon size={16} className="cht-text-blue-600" />
                    )}
                  </div>
                  <div className="cht-min-w-0">
                    <h5 className="cht-text-xs cht-font-bold cht-text-slate-800 group-hover:cht-text-[#3e57da] cht-truncate !cht-font-body cht-transition-colors">
                      {link.title}
                    </h5>
                    <p className="cht-text-[10px] cht-text-slate-400 cht-truncate !cht-font-body">
                      {link.type === "drive"
                        ? "Google Drive Link"
                        : link.type === "doc"
                        ? "Google Docs Document"
                        : link.type === "slide"
                        ? "Google Slides Presentation"
                        : link.type === "sheet"
                        ? "Google Sheets Spreadsheet"
                        : link.type === "form"
                        ? "Google Forms"
                        : link.url}
                    </p>
                  </div>
                </div>

                <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-slate-50 group-hover:cht-bg-blue-50 cht-text-slate-400 group-hover:cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-transition-colors cht-shrink-0">
                  <ExternalLink size={13} />
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Cinema Video Modal ---
const VideoModal = ({
  isOpen,
  onClose,
  videoUrl,
  title,
  duration,
  isCompleted,
  onToggleComplete,
}: {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title: string;
  duration?: string;
  isCompleted?: boolean;
  onToggleComplete?: () => void;
}) => {
  const ytId = useMemo(() => getYouTubeID(videoUrl), [videoUrl]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="cht-fixed cht-inset-0 cht-z-[9999] cht-flex cht-items-center cht-justify-center cht-p-3 sm:cht-p-6">
      {/* Backdrop overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="cht-fixed cht-inset-0 cht-bg-slate-950/85 cht-backdrop-blur-md"
      />

      {/* Modal Dialog */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="cht-relative cht-w-full cht-max-w-4xl cht-bg-slate-900 cht-border cht-border-slate-800 cht-rounded-3xl cht-overflow-hidden cht-shadow-2xl cht-z-10 cht-flex cht-flex-col"
      >
        {/* Header Bar */}
        <div className="cht-p-4 sm:cht-px-6 cht-border-b cht-border-slate-800/90 cht-flex cht-items-center cht-justify-between cht-gap-4 cht-bg-slate-900/95">
          <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
            <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-wider cht-text-rose-400 cht-bg-rose-500/10 cht-border cht-border-rose-500/20 cht-px-2.5 cht-py-1 cht-rounded-full !cht-font-body cht-shrink-0">
              <Play size={8} className="cht-fill-rose-400" />
              Video Lesson
            </span>
            <h3 className="cht-text-sm sm:cht-text-base cht-font-bold cht-text-white !cht-font-body cht-truncate">
              {title}
            </h3>
          </div>

          <div className="cht-flex cht-items-center cht-gap-3 cht-shrink-0">
            {duration && (
              <span className="cht-hidden sm:cht-inline-flex cht-items-center cht-gap-1.5 cht-text-xs cht-text-slate-400 !cht-font-body">
                <Clock size={12} />
                {duration}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="cht-w-8 cht-h-8 cht-rounded-xl cht-bg-slate-800/80 hover:cht-bg-slate-700 cht-text-slate-400 hover:cht-text-white cht-flex cht-items-center cht-justify-center cht-transition-colors"
              title="Close modal (Esc)"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Video Player (16:9) */}
        <div className="cht-w-full cht-aspect-video cht-bg-black cht-relative">
          {ytId ? (
            <iframe
              className="cht-w-full cht-h-full"
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="cht-w-full cht-h-full cht-flex cht-items-center cht-justify-center cht-text-slate-400 cht-text-sm">
              Video stream unavailable
            </div>
          )}
        </div>

        {/* Footer Bar */}
        <div className="cht-p-3.5 sm:cht-px-6 cht-border-t cht-border-slate-800/80 cht-flex cht-items-center cht-justify-between cht-bg-slate-950/60 cht-gap-3">
          <span className="cht-text-xs cht-text-slate-400 !cht-font-body">
            Press <kbd className="cht-bg-slate-800 cht-px-1.5 cht-py-0.5 cht-rounded cht-text-[10px] cht-text-slate-300">Esc</kbd> or click outside to exit
          </span>

          <div className="cht-flex cht-items-center cht-gap-2.5">
            {onToggleComplete && (
              <Button
                variant={isCompleted ? "outline" : "primary"}
                size="sm"
                className={`cht-rounded-xl cht-text-xs !cht-font-body ${
                  isCompleted
                    ? "cht-border-emerald-500/80 cht-text-emerald-400 hover:cht-bg-emerald-500/10"
                    : ""
                }`}
                onClick={onToggleComplete}
                leftIcon={isCompleted ? <Check size={14} /> : <CheckCircle2 size={14} />}
              >
                {isCompleted ? "Completed" : "Mark as Complete"}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="cht-rounded-xl cht-text-xs cht-text-slate-300 hover:cht-text-white hover:cht-bg-slate-800 !cht-font-body"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// --- Main Page Component ---
const CourseLearning = ({ previewMode }: { previewMode?: boolean } = {}) => {
  const { user: currentUser } = useAuthState();
  const { slug } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const courseIdParam = searchParams.get("courseId");
  const trackIdParam = searchParams.get("trackId");
  const materialIdParam = searchParams.get("materialId");
  const navigate = useNavigate();

  const isTrackRoute = location.pathname.toLowerCase().includes("track") || !!trackIdParam;

  const isStaffOrInstructor = Boolean(
    previewMode ||
    currentUser?.is_superuser ||
    currentUser?.role === "SUPERADMIN" ||
    currentUser?.role === "ADMIN" ||
    currentUser?.role === "INSTRUCTOR" ||
    (Array.isArray(currentUser?.roles) &&
      currentUser.roles.some((r: string) =>
        ["SUPERADMIN", "ADMIN", "INSTRUCTOR"].includes(String(r).toUpperCase())
      )) ||
    location.pathname.startsWith("/superadmin") ||
    location.pathname.startsWith("/instructor") ||
    location.pathname.startsWith("/staff")
  );

  const handleReturn = () => {
    if (location.pathname.startsWith("/superadmin")) {
      navigate(isTrackRoute ? "/superadmin/dashboard/tracks" : "/superadmin/dashboard/courses");
    } else if (location.pathname.startsWith("/instructor")) {
      navigate("/instructor/dashboard/my-courses");
    } else if (location.pathname.startsWith("/staff")) {
      navigate(isTrackRoute ? "/staff/dashboard/tracks" : "/staff/dashboard/courses");
    } else {
      navigate("/student/dashboard/my-courses");
    }
  };

  const [contents, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string | number>>(new Set());
  const [expandedLessonId, setExpandedLessonId] = useState<string | number | null>(null);

  // Selected course filter within a Career Track ("all" or courseId)
  const [selectedCourseId, setSelectedCourseId] = useState<string | number | "all">("all");

  // Quick Notes Drawer State
  const [showNotesDrawer, setShowNotesDrawer] = useState(false);
  const [notes, setNotes] = useState("");

  // Detailed Material Page Video Player State
  const [showDetailedVideoPlayer, setShowDetailedVideoPlayer] = useState(false);
  const [playingInlineVideoId, setPlayingInlineVideoId] = useState<string | number | null>(null);

  // Active Video Modal State
  const [activeVideoModal, setActiveVideoModal] = useState<{
    url: string;
    title: string;
    duration?: string;
    lessonId?: string | number;
  } | null>(null);

  // Local Comments State for Material Detail
  const [commentsMap, setCommentsMap] = useState<Record<string, Array<{ author: string; text: string; time: string }>>>({
    default: []
  });
  const [newCommentText, setNewCommentText] = useState("");

  // Error States
  const [accessError, setAccessError] = useState<string | null>(null);
  const [lockedItemDetails, setLockedItemDetails] = useState<any | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [notFoundError, setNotFoundError] = useState<string | null>(null);

  const { addToCart, loadingAddCart } = useCart();
  const { addToast } = useToast();

  // Sync Progress with Backend
  const updateProgressOnBackend = async (lessonId: string | number) => {
    if (!lessonId) return;
    try {
      const client = getClient();
      const payload: any = {
        lesson_id: String(lessonId),
      };
      if (courseIdParam) payload.course_id = courseIdParam;
      if (trackIdParam) payload.track_id = trackIdParam;

      // Fallback if param missing from query string but loaded in contents
      if (!payload.course_id && !payload.track_id && contents) {
        if (contents.type === "track") {
          payload.track_id = contents.id;
        } else {
          payload.course_id = contents.id;
        }
      }

      const response = await client.post("/course/bk-update-progress/", payload);
      if (response.data && response.data.completed_lessons) {
        setCompletedLessons(new Set(response.data.completed_lessons.map(String)));
      }
    } catch (err) {
      console.error("Failed to sync progress with backend:", err);
    }
  };

  // Load Learning Curriculum
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const client = getClient();
        let courseId = courseIdParam;
        let trackId = trackIdParam;

        // Direct slug pass-through to /bk-learning-content/ without expensive pre-round-trips
        if (!courseId && !trackId && slug) {
          if (isTrackRoute) {
            trackId = slug;
          } else {
            courseId = slug;
          }
        }

        if (!courseId && !trackId) {
          setLoading(false);
          return;
        }

        const params: any = {};
        if (courseId) params.course_id = courseId;
        if (trackId) params.track_id = trackId;

        let response;
        try {
          response = await client.get("/course/bk-learning-content/", { params });
        } catch (initialErr: any) {
          // If the initial route guess (course vs track by slug) returned 404, fallback to the other mode
          if (slug && !courseIdParam && !trackIdParam && initialErr.response?.status === 404) {
            const fallbackParams: any = isTrackRoute ? { course_id: slug } : { track_id: slug };
            response = await client.get("/course/bk-learning-content/", { params: fallbackParams });
          } else {
            throw initialErr;
          }
        }

        if (response.data) {
          setContent(response.data);

          if (response.data.completed_lessons) {
            setCompletedLessons(new Set(response.data.completed_lessons.map(String)));
          }

          // Expand all modules by default
          const mods = response.data.type === "track"
            ? (response.data.course_in_track || []).flatMap((c: any) => c.modules || [])
            : response.data.modules || [];
          
          const modIds = new Set<string | number>(mods.map((m: any, idx: number) => (m.id ?? idx) as string | number));
          setExpandedModules(modIds);

          // Find first uncompleted lesson to auto-expand in list
          const all = mods.flatMap((m: any) => m.lessons || []);
          if (all.length > 0 && !materialIdParam) {
            const completedSet = new Set((response.data.completed_lessons || []).map(String));
            const firstUncompleted = all.find((l: any) => !completedSet.has(String(l.id)));
            if (firstUncompleted) {
              setExpandedLessonId(firstUncompleted.id);
            } else {
              setExpandedLessonId(all[0].id);
            }
          }
        }
      } catch (err: any) {
        console.error("Failed to load learning content:", err);
        if (err.response && (err.response.status === 403 || err.response.status === 401)) {
          if (isStaffOrInstructor) {
            setAccessError("Curriculum preview is currently unavailable or requires platform authorization.");
          } else {
            const detailMsg = err.response.data?.detail || "You are not enrolled or payment is pending for this course.";
            setAccessError(detailMsg);

            setLoadingDetails(true);
            try {
              const client = getClient();
              const targetIdentifier = courseIdParam || trackIdParam || slug;
              if (courseIdParam || (!trackIdParam && !isTrackRoute)) {
                const res = await client.get(`/course/bk-course/${targetIdentifier}/`);
                if (res.data) setLockedItemDetails({ ...res.data, type: "course" });
              } else {
                const res = await client.get(`/course/bk-track/${targetIdentifier}/`);
                if (res.data) setLockedItemDetails({ ...res.data, type: "track" });
              }
            } catch (detailsErr) {
              console.error("Failed to fetch locked item details:", detailsErr);
            } finally {
              setLoadingDetails(false);
            }
          }
        } else if (
          err.response &&
          (err.response.status === 404 || err.response.data?.detail === "Not found.")
        ) {
          setNotFoundError("The requested course or track could not be found.");
        }
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [courseIdParam, trackIdParam, slug, isTrackRoute]);

  // Structured list of courses in a Career Track
  const coursesInTrack = useMemo(() => {
    if (!contents || contents.type !== "track") return [];
    return (contents.course_in_track || []).map((course: any, index: number) => {
      const cModules = (course.modules || []).map((m: any) => ({
        ...m,
        courseId: course.id,
        courseTitle: course.title,
        courseTemplateRepo: course.autograder_template_repo,
        coursePhase: index + 1
      }));
      const cLessons = cModules.flatMap((m: any) => [
        ...(m.lessons || []).map((l: any) => ({
          ...l,
          itemType: "lesson",
          moduleTitle: m.title,
          moduleId: m.id,
          courseTitle: course.title,
          courseId: course.id,
          courseTemplateRepo: course.autograder_template_repo,
          coursePhase: index + 1
        })),
        ...(m.exercises || []).map((e: any) => ({
          ...e,
          itemType: "exercise",
          moduleTitle: m.title,
          moduleId: m.id,
          courseTitle: course.title,
          courseId: course.id,
          courseTemplateRepo: course.autograder_template_repo,
          coursePhase: index + 1
        }))
      ]);
      const totalLessons = cLessons.length;
      const completed = cLessons.filter((l: any) => completedLessons.has(String(l.id))).length;
      const progressPercent = totalLessons > 0 ? Math.round((completed / totalLessons) * 100) : 0;
      return {
        ...course,
        phaseNumber: index + 1,
        modules: cModules,
        lessons: cLessons,
        totalLessons,
        completedCount: completed,
        progressPercent,
        isCompleted: totalLessons > 0 && completed === totalLessons,
      };
    });
  }, [contents, completedLessons]);

  // Auto-select first uncompleted course in track on first load
  useEffect(() => {
    if (contents?.type === "track" && coursesInTrack.length > 0 && selectedCourseId === "all") {
      const active = coursesInTrack.find((c: any) => !c.isCompleted) || coursesInTrack[0];
      if (active) {
        setSelectedCourseId(active.id);
      }
    }
  }, [contents, coursesInTrack]);

  // Active selected course when in Career Track
  const activeCourse = useMemo(() => {
    if (contents?.type !== "track" || selectedCourseId === "all") return null;
    return coursesInTrack.find((c: any) => String(c.id) === String(selectedCourseId)) || null;
  }, [contents, selectedCourseId, coursesInTrack]);

  // Derived modules for the current view
  const modules = useMemo(() => {
    if (!contents) return [];
    if (contents.type === "track") {
      if (activeCourse) {
        return activeCourse.modules || [];
      }
      return coursesInTrack.flatMap((c: any) => c.modules || []);
    }
    return contents.modules || [];
  }, [contents, activeCourse, coursesInTrack]);

  // All lessons across the entire enrollment (track or standalone course)
  const allLessons = useMemo(() => {
    if (!contents) return [];
    if (contents.type === "track") {
      return coursesInTrack.flatMap((c: any) => c.lessons);
    }
    return (contents.modules || []).flatMap((m: any) => [
      ...(m.lessons || []).map((l: any) => ({
        ...l,
        itemType: "lesson",
        moduleTitle: m.title,
        moduleId: m.id,
        courseId: contents.id,
        courseTemplateRepo: contents.autograder_template_repo,
      })),
      ...(m.exercises || []).map((e: any) => ({
        ...e,
        itemType: "exercise",
        moduleTitle: m.title,
        moduleId: m.id,
        courseId: contents.id,
        courseTemplateRepo: contents.autograder_template_repo,
      }))
    ]);
  }, [contents, coursesInTrack]);

  const totalLessonsCount = allLessons.length;
  const completedCount = completedLessons.size;
  const progressPercent = totalLessonsCount > 0
    ? Math.round((completedCount / totalLessonsCount) * 100)
    : 0;

  // Active Material for the Detailed Page
  const currentMaterial = useMemo(() => {
    if (!materialIdParam) return null;
    return allLessons.find((item: any) => String(item.id) === String(materialIdParam)) || null;
  }, [materialIdParam, allLessons]);

  const currentMaterialIndex = useMemo(() => {
    if (!currentMaterial) return -1;
    return allLessons.findIndex((item: any) => String(item.id) === String(currentMaterial.id));
  }, [currentMaterial, allLessons]);

  // Next incomplete lesson for quick "Resume Learning"
  const nextUncompletedLesson = useMemo(() => {
    return allLessons.find((l: any) => !completedLessons.has(String(l.id))) || allLessons[0] || null;
  }, [allLessons, completedLessons]);

  // Toggle Module Open / Closed
  const toggleModule = (modId: string | number) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(modId)) {
        next.delete(modId);
      } else {
        next.add(modId);
      }
      return next;
    });
  };

  // Toggle Lesson Card Expansion
  const toggleLesson = (lessonId: string | number) => {
    setExpandedLessonId((prev) => (prev === lessonId ? null : lessonId));
  };

  // Expand / Collapse all modules
  const handleToggleAllModules = () => {
    if (expandedModules.size === modules.length) {
      setExpandedModules(new Set());
    } else {
      setExpandedModules(new Set(modules.map((m: any, idx: number) => m.id || idx)));
    }
  };

  // Navigate to Detailed Material Page (View Material)
  const openMaterialDetail = (item: any) => {
    setShowDetailedVideoPlayer(false);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("materialId", String(item.id));
    navigate(`?${nextParams.toString()}`);
  };

  // Back to Classwork Curriculum
  const backToCurriculum = () => {
    setShowDetailedVideoPlayer(false);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("materialId");
    navigate(`?${nextParams.toString()}`);
  };

  // Navigate between materials on the Detailed Page
  const handleNavigateMaterial = (targetId: string | number) => {
    setShowDetailedVideoPlayer(false);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("materialId", String(targetId));
    navigate(`?${nextParams.toString()}`);
  };

  // Next Lesson Action on Classwork List
  const handleNextLesson = (currentId: string | number) => {
    const currentIdx = allLessons.findIndex((l: any) => String(l.id) === String(currentId));
    if (currentIdx >= 0 && currentIdx < allLessons.length - 1) {
      const nextItem = allLessons[currentIdx + 1];
      setExpandedLessonId(nextItem.id);

      // If next item belongs to a different course in the track, switch active course
      if (contents?.type === "track" && nextItem.courseId && selectedCourseId !== "all" && String(selectedCourseId) !== String(nextItem.courseId)) {
        setSelectedCourseId(nextItem.courseId);
      }

      // Ensure module containing next lesson is expanded
      const parentMod = modules.find((m: any) => (m.lessons || []).some((l: any) => String(l.id) === String(nextItem.id)));
      if (parentMod) {
        setExpandedModules((prev) => new Set(prev).add(parentMod.id));
      }
    } else {
      addToast({
        message: "Congratulations! You have viewed all materials in this curriculum.",
        bg: "success",
      });
    }
  };

  // Toggle Lesson Completion
  const handleToggleCompletion = async (lessonId: string | number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const idStr = String(lessonId);
    await updateProgressOnBackend(lessonId);

    setCompletedLessons((prev) => {
      const next = new Set(prev);
      if (next.has(idStr)) {
        next.delete(idStr);
      } else {
        next.add(idStr);
      }
      return next;
    });
  };

  // Add Comment on Detailed Material Page
  const handleAddComment = () => {
    if (!newCommentText.trim() || !currentMaterial) return;
    const key = String(currentMaterial.id);
    const authorName = currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ""}`.trim() : "Student";
    const commentItem = {
      author: authorName,
      text: newCommentText.trim(),
      time: "Just now"
    };

    setCommentsMap((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), commentItem]
    }));
    setNewCommentText("");
  };

  // --- Render Restricted Access Modal ---
  if (accessError) {
    if (isStaffOrInstructor) {
      return (
        <div className="cht-p-10 cht-max-w-md cht-mx-auto cht-text-center cht-space-y-6 cht-pt-24">
          <div className="cht-w-16 cht-h-16 cht-bg-indigo-50 cht-text-[#3e57da] cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-shadow-sm">
            <AlertCircle size={32} />
          </div>
          <h3 className="cht-text-xl cht-font-black cht-text-slate-900 !cht-font-heading">
            Preview Unavailable
          </h3>
          <p className="cht-text-sm cht-text-slate-500 cht-font-medium leading-relaxed">
            {accessError}
          </p>
          <Button variant="primary" onClick={handleReturn} className="cht-rounded-xl">
            Return to Dashboard
          </Button>
        </div>
      );
    }

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="cht-fixed cht-inset-0 cht-z-[9999] cht-flex cht-items-center cht-justify-center cht-bg-slate-950/70 cht-backdrop-blur-xl cht-p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="cht-bg-white/95 cht-backdrop-blur-2xl cht-w-full cht-max-w-lg cht-rounded-[36px] cht-p-8 sm:cht-p-10 cht-shadow-[0_32px_64px_rgba(0,0,0,0.15)] cht-border cht-border-white/50 cht-space-y-6 cht-text-center cht-relative cht-overflow-hidden"
          >
            <div className="cht-absolute cht-top-0 cht-left-0 cht-right-0 cht-h-1.5 cht-bg-gradient-to-r cht-from-[#3e57da] cht-via-blue-500 cht-to-indigo-600" />

            <div className="cht-w-20 cht-h-20 cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-rounded-[28px] cht-flex cht-items-center cht-justify-center cht-mx-auto cht-shadow-inner cht-relative">
              <div className="cht-absolute cht-inset-0 cht-rounded-[28px] cht-bg-[#3e57da]/10 cht-animate-ping cht-opacity-25" />
              <svg className="cht-w-9 cht-h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>

            <div className="cht-space-y-2">
              <h3 className="cht-text-xl sm:cht-text-2xl cht-font-black cht-text-black cht-tracking-tight">Access Restricted</h3>
              <p className="cht-text-sm cht-text-gray-500 cht-font-medium leading-relaxed">{accessError}</p>
            </div>

            {loadingDetails ? (
              <div className="cht-bg-slate-50 cht-p-6 cht-rounded-2xl cht-flex cht-flex-col cht-items-center cht-justify-center cht-space-y-2">
                <div className="cht-w-6 cht-h-6 cht-border-2 cht-border-[#3e57da] cht-border-t-transparent cht-rounded-full cht-animate-spin" />
                <p className="cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-uppercase">Loading course details...</p>
              </div>
            ) : lockedItemDetails ? (
              <div className="cht-bg-slate-50 cht-p-5 cht-rounded-[24px] cht-border cht-border-slate-100 cht-text-left cht-space-y-4">
                <div className="cht-flex cht-items-start cht-gap-4">
                  {lockedItemDetails.course_img_url || lockedItemDetails.track_img_url ? (
                    <img
                      src={lockedItemDetails.course_img_url || lockedItemDetails.track_img_url}
                      alt={lockedItemDetails.title}
                      className="cht-w-16 cht-h-16 cht-object-cover cht-rounded-[16px] cht-border cht-border-slate-200"
                    />
                  ) : (
                    <div className="cht-w-16 cht-h-16 cht-bg-[#3e57da]/10 cht-rounded-[16px] cht-flex cht-items-center cht-justify-center cht-text-[#3e57da]">
                      <BookOpen size={24} />
                    </div>
                  )}
                  <div className="cht-flex-1">
                    <span className="cht-px-2 cht-py-0.5 cht-text-[9px] cht-font-bold cht-text-[#3e57da] cht-bg-[#3e57da]/10 cht-rounded-full cht-uppercase">
                      {lockedItemDetails.type}
                    </span>
                    <h4 className="cht-text-sm cht-font-bold cht-text-slate-800 cht-line-clamp-2 cht-mt-1">{lockedItemDetails.title}</h4>
                  </div>
                </div>

                <div className="cht-pt-3 cht-border-t cht-border-slate-200/60 cht-flex cht-justify-between cht-text-xs">
                  <div>
                    <span className="cht-text-[10px] cht-text-slate-400 cht-uppercase">Price</span>
                    <p className="cht-text-sm cht-font-bold cht-text-slate-900 cht-mt-0.5">
                      ₦{lockedItemDetails.online_price?.toLocaleString()}
                    </p>
                  </div>
                  {lockedItemDetails.instructor && (
                    <div className="cht-text-right">
                      <span className="cht-text-[10px] cht-text-slate-400 cht-uppercase">Instructor</span>
                      <p className="cht-text-xs cht-font-bold cht-text-slate-800 cht-mt-0.5">{lockedItemDetails.instructor.name}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            <div className="cht-flex cht-flex-col sm:cht-flex-row cht-gap-3 cht-pt-2">
              <Button
                variant="primary"
                className="cht-flex-1 cht-py-3.5 cht-rounded-[16px]"
                disabled={loadingAddCart === (lockedItemDetails?.title || "")}
                onClick={async () => {
                  if (lockedItemDetails) {
                    await addToCart(
                      lockedItemDetails.id,
                      lockedItemDetails.type === "course" ? "course" : "coursetrack",
                      "online",
                      lockedItemDetails.title
                    );
                  }
                  navigate("/student/dashboard/payments");
                }}
              >
                {loadingAddCart === (lockedItemDetails?.title || "") ? "Adding..." : "Unlock Course"}
              </Button>
              <Button
                variant="outline"
                className="cht-flex-1 cht-py-3.5 cht-rounded-[16px]"
                onClick={() => navigate("/student/dashboard/my-courses")}
              >
                My Courses
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // --- Render Not Found State ---
  if (notFoundError) {
    return (
      <div className="cht-p-10 cht-max-w-md cht-mx-auto cht-text-center cht-space-y-6 cht-pt-24">
        <div className="cht-w-16 cht-h-16 cht-bg-orange-50 cht-text-orange-600 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto">
          <AlertCircle size={32} />
        </div>
        <h3 className="cht-text-xl cht-font-bold cht-text-slate-900 !cht-font-heading">Curriculum Not Found</h3>
        <p className="cht-text-sm cht-text-slate-500">{notFoundError}</p>
        <Button variant="primary" onClick={handleReturn} className="cht-rounded-xl">
          {isStaffOrInstructor ? "Return to Dashboard" : "Return to My Learning"}
        </Button>
      </div>
    );
  }

  // --- Render Loading State ---
  if (loading) {
    return (
      <div className="cht-flex cht-flex-col cht-items-center cht-justify-center cht-min-h-[500px] cht-space-y-4">
        <div className="cht-w-12 cht-h-12 cht-border-4 cht-border-[#3e57da] cht-border-t-transparent cht-rounded-full cht-animate-spin" />
        <p className="cht-text-xs cht-font-bold cht-text-slate-400 cht-uppercase cht-tracking-widest">
          Loading Classwork...
        </p>
      </div>
    );
  }

  // =========================================================================
  // VIEW 1: DETAILED MATERIAL PAGE (When user clicks "View material")
  // Replicates the user's uploaded detailed view screenshot (media_1788558778916.png)
  // =========================================================================
  if (currentMaterial) {
    const isCompleted = completedLessons.has(String(currentMaterial.id));
    const postedDate = formatDetailedDate(currentMaterial.date_created || currentMaterial.date);
    const editedDate = formatEditedDate(currentMaterial.date_created || currentMaterial.date);
    const materialComments = commentsMap[String(currentMaterial.id)] || [];
    const isVideo = !!currentMaterial.video_url;
    const isExercise = currentMaterial.itemType === "exercise";
    const ytId = isVideo ? getYouTubeID(currentMaterial.video_url) : null;

    return (
      <div className="cht-max-w-4xl cht-mx-auto cht-space-y-6 cht-pb-20 cht-animate-in cht-fade-in cht-duration-300">
        
        {/* Back navigation button */}
        <div className="cht-flex cht-items-center cht-justify-between">
          <button
            onClick={backToCurriculum}
            className="cht-inline-flex cht-items-center cht-gap-2 cht-text-sm cht-font-semibold cht-text-slate-600 hover:cht-text-[#3e57da] cht-transition-colors cht-p-1"
          >
            <ChevronLeft size={18} strokeWidth={2.5} />
            Back to {contents?.title || "Classwork"}
          </button>

          {/* Quick Notes Toggle */}
          <button
            onClick={() => setShowNotesDrawer(!showNotesDrawer)}
            className={`cht-w-9 cht-h-9 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-transition-colors ${
              showNotesDrawer
                ? "cht-bg-[#3e57da] cht-text-white"
                : "cht-bg-white cht-text-slate-600 cht-border cht-border-gray-200 hover:cht-bg-slate-50"
            }`}
            title="Personal Notes"
          >
            <StickyNote size={18} />
          </button>
        </div>

        {/* Faculty & Staff Preview Banner */}
        {(isStaffOrInstructor || contents?.is_preview) && (
          <div className="cht-bg-gradient-to-r cht-from-slate-900 cht-via-indigo-950 cht-to-[#3e57da] cht-rounded-[20px] cht-p-3.5 sm:cht-p-4 cht-text-white cht-shadow-md cht-flex cht-items-center cht-justify-between cht-gap-3 cht-border cht-border-indigo-400/20">
            <div className="cht-flex cht-items-center cht-gap-3">
              <Sparkles size={16} className="cht-text-amber-300 cht-shrink-0" />
              <p className="cht-text-xs cht-text-indigo-100 font-medium">
                <span className="cht-font-bold cht-text-white">Previewing Lesson:</span> Authorized faculty view (no paid student access restriction).
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleReturn}
              className="cht-rounded-xl cht-text-[11px] cht-bg-white/10 cht-border-white/30 cht-text-white hover:cht-bg-white hover:cht-text-slate-900 cht-shrink-0"
            >
              Exit Preview
            </Button>
          </div>
        )}

        {/* Detailed Material Card */}
        <div className="cht-bg-white cht-p-6 sm:cht-p-8 md:cht-p-10 cht-rounded-[24px] cht-border cht-border-slate-200/80 cht-shadow-[0_4px_24px_rgba(0,0,0,0.03)] cht-space-y-6">
          
          {/* Top Title Row: Modern Badge + Title + Options Menu */}
          <div className="cht-flex cht-items-start cht-justify-between cht-gap-4">
            <div className="cht-flex cht-items-center cht-gap-4 cht-min-w-0">
              <div className={`cht-w-12 cht-h-12 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-shrink-0 ${
                isVideo
                  ? "cht-bg-rose-50 cht-text-rose-600 cht-border cht-border-rose-100/80 cht-shadow-sm"
                  : isExercise
                  ? "cht-bg-amber-50 cht-text-amber-600 cht-border cht-border-amber-100/80 cht-shadow-sm"
                  : "cht-bg-sky-50 cht-text-sky-600 cht-border cht-border-sky-100/80 cht-shadow-sm"
              }`}>
                {isVideo ? (
                  <Play size={20} className="cht-fill-rose-600" />
                ) : isExercise ? (
                  <ClipboardList size={22} strokeWidth={2} />
                ) : (
                  <BookOpen size={22} strokeWidth={2} />
                )}
              </div>

              <div className="cht-min-w-0">
                <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
                  {isVideo ? (
                    <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-[11px] cht-font-bold cht-text-rose-600 cht-bg-rose-50 cht-border cht-border-rose-200/70 cht-px-2.5 cht-py-0.5 cht-rounded-full !cht-font-body">
                      <Play size={9} className="cht-fill-rose-500" />
                      Video Lesson
                    </span>
                  ) : isExercise ? (
                    <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-[11px] cht-font-bold cht-text-amber-700 cht-bg-amber-50 cht-border cht-border-amber-200/70 cht-px-2.5 cht-py-0.5 cht-rounded-full !cht-font-body">
                      <ClipboardList size={10} />
                      Assignment
                    </span>
                  ) : (
                    <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-[11px] cht-font-bold cht-text-sky-700 cht-bg-sky-50 cht-border cht-border-sky-200/70 cht-px-2.5 cht-py-0.5 cht-rounded-full !cht-font-body">
                      <BookOpen size={10} />
                      Reading & Notes
                    </span>
                  )}
                  {currentMaterial.duration && (
                    <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[11px] cht-text-slate-500 cht-font-medium !cht-font-body">
                      <Clock size={11} className="cht-text-slate-400" />
                      {currentMaterial.duration}
                    </span>
                  )}
                </div>
                <h1 className="cht-text-2xl sm:cht-text-3xl cht-font-bold cht-text-slate-900 !cht-font-body cht-tracking-tight">
                  {currentMaterial.title}
                </h1>
              </div>
            </div>

            <div className="cht-flex cht-items-center cht-gap-2.5 cht-shrink-0">
              <Button
                variant={isCompleted ? "outline" : "primary"}
                size="sm"
                className={`cht-rounded-xl cht-text-[11px] !cht-font-body ${
                  isCompleted
                    ? "cht-border-emerald-500 cht-text-emerald-700 hover:cht-bg-emerald-50"
                    : ""
                }`}
                onClick={(e: any) => handleToggleCompletion(currentMaterial.id, e)}
                leftIcon={isCompleted ? <Check size={14} /> : <CheckCircle2 size={14} />}
              >
                {isCompleted ? "Completed" : "Mark Complete"}
              </Button>
            </div>
          </div>

          {/* Subtitle: Author • Date */}
          <div className="cht-text-xs sm:cht-text-[13px] cht-text-slate-500 cht-font-medium !cht-font-body cht-flex cht-items-center cht-gap-2">
            <span>Codehub Technologies</span>
            <span className="cht-text-slate-300">•</span>
            <span>{postedDate}</span>
          </div>

          {/* Subtle 1px Horizontal Divider */}
          <div className="cht-border-b cht-border-slate-100" />

          {/* Main Body Content: Rich Text / Code Snippets */}
          {(currentMaterial.content || currentMaterial.exercise_task) && (
            <div className="cht-space-y-3">
              {isVideo && currentMaterial.content && (
                <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-font-bold cht-text-slate-500 cht-uppercase cht-tracking-wider !cht-font-body">
                  <FileText size={13} className="cht-text-slate-400" />
                  <span>Lesson Notes & Overview</span>
                </div>
              )}
              <div 
                key={`material-body-${currentMaterial?.id || currentMaterialIndex}`}
                className="cht-prose cht-prose-slate cht-max-w-none cht-text-slate-800 cht-text-sm sm:cht-text-[15px] cht-leading-relaxed !cht-font-body [&_h1]:cht-text-xl [&_h1]:cht-font-bold [&_h1]:!cht-font-body [&_h1]:cht-text-slate-900 [&_h2]:cht-text-lg [&_h2]:cht-font-bold [&_h2]:!cht-font-body [&_h2]:cht-text-slate-900 [&_h3]:cht-text-base [&_h3]:cht-font-bold [&_h3]:!cht-font-body [&_h3]:cht-text-slate-900 [&_p]:cht-my-3.5 [&_ul]:cht-list-disc [&_ul]:cht-pl-6 [&_ul]:cht-my-3 [&_li]:cht-my-1.5 [&_strong]:cht-font-semibold [&_strong]:cht-text-slate-900 [&_code]:cht-bg-slate-100 [&_code]:cht-text-slate-900 [&_code]:cht-px-1.5 [&_code]:cht-py-0.5 [&_code]:cht-rounded-md [&_pre]:cht-bg-slate-900 [&_pre]:cht-text-slate-100 [&_pre]:cht-p-5 [&_pre]:cht-rounded-2xl">
                {parse(currentMaterial.content || currentMaterial.exercise_task || "")}
              </div>
            </div>
          )}

          {/* If Exercise, show assignment submission section */}
          {isExercise && (
            <ExerciseSubmission
              exercise={currentMaterial}
              courseId={currentMaterial.courseId || contents?.id}
              moduleId={currentMaterial.moduleId}
              track={contents?.type === "track" ? contents?.title : undefined}
              courseTemplateRepo={currentMaterial.courseTemplateRepo || contents?.autograder_template_repo}
            />
          )}

          {/* Topic Attachments & Learning Materials (Videos, Files, Links, Google Docs/Drive) */}
          {!isExercise && (
            <TopicAttachmentsSection
              material={currentMaterial}
              onOpenVideoModal={(video) =>
                setActiveVideoModal({
                  url: video.url,
                  title: video.title || currentMaterial.title || "Learn with Video",
                  duration: video.duration || currentMaterial.duration,
                  lessonId: currentMaterial.id,
                })
              }
            />
          )}

          {/* Class Comments Section (Matches Screenshot media_1788558778916.png) */}
          <div className="cht-pt-6 cht-border-t cht-border-gray-200 cht-space-y-4">
            
            {/* Header: Users icon + "Class comments" */}
            <div className="cht-flex cht-items-center cht-gap-2.5 cht-text-slate-800">
              <Users size={19} strokeWidth={2} className="cht-text-slate-700" />
              <h3 className="cht-text-sm cht-font-medium">Class comments</h3>
              {materialComments.length > 0 && (
                <span className="cht-text-xs cht-text-slate-400">({materialComments.length})</span>
              )}
            </div>

            {/* List of comments */}
            {materialComments.length > 0 && (
              <div className="cht-space-y-3 cht-pl-2">
                {materialComments.map((comment, idx) => (
                  <div key={idx} className="cht-flex cht-items-start cht-gap-3 cht-text-sm">
                    <div className="cht-w-8 cht-h-8 cht-rounded-full cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-text-xs cht-font-bold cht-shrink-0">
                      {comment.author?.[0] || "S"}
                    </div>
                    <div className="cht-flex-1 cht-bg-slate-50 cht-rounded-2xl cht-p-3.5 cht-border cht-border-slate-100">
                      <div className="cht-flex cht-items-center cht-justify-between cht-text-xs">
                        <span className="cht-font-bold cht-text-slate-800">{comment.author}</span>
                        <span className="cht-text-slate-400">{comment.time}</span>
                      </div>
                      <p className="cht-text-xs sm:cht-text-sm cht-text-slate-700 cht-mt-1 leading-relaxed">
                        {comment.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Comment Row (MessageSquare icon + "Add comment") */}
            <div className="cht-flex cht-items-center cht-gap-3 cht-pt-1">
              <MessageSquare size={17} className="cht-text-[#1a73e8] cht-shrink-0" />
              <input
                type="text"
                placeholder="Add comment..."
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                className="cht-flex-1 cht-bg-transparent cht-border-b cht-border-gray-200 focus:cht-border-[#1a73e8] cht-py-2 cht-px-1 cht-text-xs sm:cht-text-sm cht-text-slate-800 cht-outline-none cht-transition-colors"
              />
              {newCommentText.trim() && (
                <Button
                  size="sm"
                  variant="primary"
                  className="cht-rounded-full cht-text-[11px] cht-px-4"
                  onClick={handleAddComment}
                  rightIcon={<Send size={12} />}
                >
                  Post
                </Button>
              )}
            </div>
          </div>

          {/* Footer Navigation: Previous / Next Material */}
          <div className="cht-pt-6 cht-border-t cht-border-gray-200 cht-flex cht-items-center cht-justify-between">
            <Button
              variant="ghost"
              size="sm"
              disabled={currentMaterialIndex <= 0}
              onClick={() => {
                if (currentMaterialIndex > 0) {
                  handleNavigateMaterial(allLessons[currentMaterialIndex - 1].id);
                }
              }}
              leftIcon={<ChevronLeft size={16} />}
              className="cht-rounded-xl cht-text-[11px]"
            >
              Previous Lesson
            </Button>

            <Button
              variant="primary"
              size="sm"
              disabled={currentMaterialIndex >= allLessons.length - 1}
              onClick={() => {
                if (currentMaterialIndex < allLessons.length - 1) {
                  handleNavigateMaterial(allLessons[currentMaterialIndex + 1].id);
                }
              }}
              rightIcon={<ChevronRight size={16} />}
              className="cht-rounded-xl cht-text-[11px]"
            >
              Next Lesson
            </Button>
          </div>

        </div>

        {/* Quick Notes Side Drawer */}
        <AnimatePresence>
          {showNotesDrawer && (
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className="cht-fixed cht-right-6 cht-top-24 cht-bottom-12 cht-w-80 sm:cht-w-96 cht-bg-white cht-rounded-[24px] cht-border cht-border-gray-200 cht-shadow-2xl cht-p-6 cht-flex cht-flex-col cht-z-50"
            >
              <div className="cht-flex cht-items-center cht-justify-between cht-pb-4 cht-border-b cht-border-gray-100">
                <div className="cht-flex cht-items-center cht-gap-2.5">
                  <StickyNote size={18} className="cht-text-[#3e57da]" />
                  <h4 className="cht-text-sm cht-font-bold cht-text-slate-900">Personal Notes</h4>
                </div>
                <button
                  onClick={() => setShowNotesDrawer(false)}
                  className="cht-text-slate-400 hover:cht-text-slate-700"
                >
                  <X size={18} />
                </button>
              </div>

              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Take notes while studying..."
                className="cht-flex-1 cht-w-full cht-bg-slate-50 cht-rounded-xl cht-p-4 cht-text-xs cht-font-medium cht-text-slate-700 cht-border cht-border-slate-100 cht-outline-none cht-resize-none cht-mt-4 focus:cht-bg-white focus:cht-border-[#3e57da] cht-transition-all"
              />

              <div className="cht-pt-3 cht-flex cht-items-center cht-justify-between cht-text-[10px] cht-text-slate-400">
                <span>Auto-saved locally</span>
                <span>{notes.length} characters</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video Modal Overlay */}
        {activeVideoModal && (
          <VideoModal
            isOpen={!!activeVideoModal}
            videoUrl={activeVideoModal.url}
            title={activeVideoModal.title}
            duration={activeVideoModal.duration}
            isCompleted={completedLessons.has(String(activeVideoModal.lessonId))}
            onToggleComplete={() => {
              if (activeVideoModal.lessonId) {
                handleToggleCompletion(activeVideoModal.lessonId);
              }
            }}
            onClose={() => setActiveVideoModal(null)}
          />
        )}

      </div>
    );
  }

  // =========================================================================
  // VIEW 2: CLASSWORK MODULE CURRICULUM LIST (Default View)
  // Replicates the user's uploaded classwork screenshots (media_1788557285803.png & media_1788557309794.png)
  // =========================================================================
  return (
    <div className="cht-max-w-5xl cht-mx-auto cht-space-y-8 cht-pb-20 cht-animate-in cht-fade-in cht-duration-500 !cht-font-body">
      
      {/* Faculty & Staff Preview Banner */}
      {(isStaffOrInstructor || contents?.is_preview) && (
        <div className="cht-bg-gradient-to-r cht-from-slate-900 cht-via-indigo-950 cht-to-[#3e57da] cht-rounded-[24px] cht-p-4 sm:cht-p-5 cht-text-white cht-shadow-xl cht-shadow-indigo-950/10 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-4 cht-border cht-border-indigo-400/20">
          <div className="cht-flex cht-items-center cht-gap-3.5">
            <div className="cht-w-10 cht-h-10 cht-rounded-2xl cht-bg-white/10 cht-backdrop-blur-md cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-border cht-border-white/10">
              <Sparkles size={18} className="cht-text-amber-300" />
            </div>
            <div>
              <div className="cht-flex cht-items-center cht-gap-2">
                <span className="cht-text-xs cht-font-black cht-tracking-wide cht-uppercase">
                  Faculty & Admin Outcome Preview
                </span>
                <span className="cht-px-2 cht-py-0.5 cht-text-[9px] cht-font-black cht-bg-white/20 cht-rounded-full">
                  Tuition Paywall Bypassed
                </span>
              </div>
              <p className="cht-text-xs cht-text-indigo-100/90 cht-mt-0.5 leading-relaxed">
                You are viewing the curriculum material directly from your dashboard. Role switching and paid student access are bypassed.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleReturn}
            className="cht-rounded-xl cht-text-xs cht-bg-white/10 cht-border-white/30 cht-text-white hover:cht-bg-white hover:cht-text-slate-900 cht-shrink-0"
          >
            Exit Preview
          </Button>
        </div>
      )}

      {/* Top Breadcrumb & Course Header */}
      <header className="cht-bg-white cht-p-6 md:cht-p-8 cht-rounded-[24px] cht-border cht-border-slate-200/80 cht-shadow-[0_4px_24px_rgba(0,0,0,0.03)] cht-space-y-6">
        <div className="cht-flex cht-flex-col lg:cht-flex-row lg:cht-items-center lg:cht-justify-between cht-gap-6">
          <div className="cht-flex cht-items-start cht-gap-4">
            <button
              type="button"
              onClick={handleReturn}
              className="cht-w-10 cht-h-10 cht-bg-slate-50 hover:cht-bg-slate-100 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-text-slate-600 hover:cht-text-slate-900 cht-border cht-border-slate-200/60 cht-transition-colors cht-shrink-0 cht-mt-1 cursor-pointer"
              title={isStaffOrInstructor ? "Back to Dashboard" : "Back to My Courses"}
            >
              <ChevronLeft size={20} strokeWidth={2} />
            </button>
            <div className="cht-space-y-1.5">
              <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
                <span className="cht-px-2.5 cht-py-0.5 cht-text-[11px] cht-font-bold cht-text-[#3e57da] cht-bg-[#3e57da]/10 cht-rounded-full cht-uppercase cht-tracking-wider !cht-font-body">
                  {contents?.type === "track" ? "Career Track" : "Course"}
                </span>
                <span className="cht-text-xs cht-text-slate-500 cht-font-medium !cht-font-body">
                  {totalLessonsCount} materials & lessons
                </span>
                <span className="cht-text-slate-300">•</span>
                <span className="cht-text-xs cht-text-slate-500 cht-font-medium !cht-font-body">
                  {modules.length} modules
                </span>
              </div>
              <h1 className="cht-text-2xl sm:cht-text-3xl lg:cht-text-4xl cht-font-bold cht-text-slate-900 !cht-font-body cht-tracking-tight">
                {contents?.title || "Curriculum"}
              </h1>
            </div>
          </div>

          {/* Quick Toolbar & Resume CTA */}
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-flex-wrap">
            {nextUncompletedLesson && (
              <Button
                variant="primary"
                size="sm"
                className="cht-rounded-xl cht-text-xs cht-font-bold cht-shadow-sm hover:cht-shadow !cht-font-body"
                onClick={() => openMaterialDetail(nextUncompletedLesson)}
                leftIcon={<Play size={14} className="cht-fill-current" />}
              >
                {completedCount === 0 ? "Start Learning" : "Resume Lesson"}
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              className="cht-rounded-xl cht-text-xs !cht-font-body"
              onClick={handleToggleAllModules}
            >
              {expandedModules.size === modules.length ? "Collapse All" : "Expand All"}
            </Button>
            
            <button
              onClick={() => setShowNotesDrawer(!showNotesDrawer)}
              className={`cht-h-9 cht-px-3 cht-rounded-xl cht-flex cht-items-center cht-gap-1.5 cht-text-xs cht-font-semibold cht-transition-colors !cht-font-body ${
                showNotesDrawer
                  ? "cht-bg-[#3e57da] cht-text-white"
                  : "cht-bg-slate-50 cht-text-slate-600 hover:cht-bg-slate-100 cht-border cht-border-slate-200/70"
              }`}
              title="Personal Study Notes"
            >
              <StickyNote size={15} />
              <span className="cht-hidden sm:cht-inline">Notes</span>
            </button>
          </div>
        </div>

        {/* Learning Progress Bar */}
        <div className="cht-space-y-2.5 cht-pt-3 cht-border-t cht-border-slate-100">
          <div className="cht-flex cht-items-center cht-justify-between cht-text-xs !cht-font-body">
            <span className="cht-text-slate-500 cht-font-medium">
              Course Progress: <span className="cht-text-slate-900 cht-font-bold">{completedCount}</span> of {totalLessonsCount} completed
            </span>
            <span className="cht-text-[#3e57da] cht-font-bold">{progressPercent}%</span>
          </div>

          {/* Modern Gradient Bar */}
          <div className="cht-w-full cht-h-2.5 cht-bg-slate-100 cht-rounded-full cht-overflow-hidden cht-p-[1px]">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="cht-h-full cht-bg-gradient-to-r cht-from-[#3e57da] cht-to-indigo-500 cht-rounded-full"
            />
          </div>

          <div className="cht-flex cht-items-center cht-justify-between cht-text-[11px] cht-text-slate-400 !cht-font-body">
            <span>
              {progressPercent === 100
                ? "🎉 You've completed this entire curriculum!"
                : `${totalLessonsCount - completedCount} lessons left to complete`}
            </span>
            {progressPercent === 100 && (
              <span className="cht-text-emerald-600 cht-font-semibold cht-flex cht-items-center cht-gap-1">
                <CheckCircle2 size={12} /> Certificate ready
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Course-Level GitHub Workspace Provisioning Banner (Standalone Course) */}
      {contents?.type !== "track" && (
        <CourseGitHubWorkspaceBanner
          courseId={contents?.id}
          courseTitle={contents?.title}
          templateRepo={contents?.autograder_template_repo}
          initialSubmission={contents?.course_submission}
          hasAutogradedExercises={allLessons.some((l: any) => l.itemType === "exercise" && l.requires_autograder)}
        />
      )}

      {/* Career Track Course Milestone Roadmap */}
      {contents?.type === "track" && coursesInTrack.length > 0 && (
        <div className="cht-space-y-4">
          <div className="cht-flex cht-items-center cht-justify-between">
            <div className="cht-flex cht-items-center cht-gap-2">
              <Layers size={18} className="cht-text-[#3e57da]" />
              <h3 className="cht-text-sm sm:cht-text-base cht-font-bold cht-text-slate-900 !cht-font-body">
                Track Courses & Milestones ({coursesInTrack.length})
              </h3>
            </div>
            
            <button
              onClick={() => setSelectedCourseId("all")}
              className={`cht-px-3.5 cht-py-1.5 cht-rounded-xl cht-text-xs cht-font-semibold !cht-font-body cht-transition-all ${
                selectedCourseId === "all"
                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                  : "cht-bg-white cht-text-slate-600 hover:cht-bg-slate-50 cht-border cht-border-slate-200/80"
              }`}
            >
              All Courses ({coursesInTrack.length})
            </button>
          </div>

          {/* Course Milestone Cards Grid */}
          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-3.5">
            {coursesInTrack.map((course: any) => {
              const isSelected = String(selectedCourseId) === String(course.id);
              return (
                <div
                  key={course.id}
                  onClick={() => {
                    setSelectedCourseId(course.id);
                    const modIds = new Set<string | number>((course.modules || []).map((m: any, idx: number) => m.id ?? idx));
                    setExpandedModules(modIds);
                  }}
                  className={`cht-p-5 cht-rounded-[22px] cht-border cht-cursor-pointer cht-transition-all cht-relative cht-overflow-hidden ${
                    isSelected
                      ? "cht-bg-white cht-border-[#3e57da] cht-shadow-[0_4px_20px_rgba(62,87,218,0.1)] cht-ring-2 cht-ring-[#3e57da]/20"
                      : "cht-bg-white/90 cht-border-slate-200/80 hover:cht-border-slate-300 hover:cht-bg-white hover:cht-shadow-sm"
                  }`}
                >
                  <div className="cht-flex cht-items-center cht-justify-between cht-gap-2">
                    <span className="cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-slate-100 cht-text-slate-600 cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-wider !cht-font-body">
                      Phase 0{course.phaseNumber}
                    </span>
                    {course.isCompleted ? (
                      <span className="cht-text-[11px] cht-font-bold cht-text-emerald-700 cht-bg-emerald-50 cht-px-2 cht-py-0.5 cht-rounded-md cht-flex cht-items-center cht-gap-1 !cht-font-body">
                        <Check size={11} strokeWidth={3} />
                        Completed
                      </span>
                    ) : course.completedCount > 0 ? (
                      <span className="cht-text-[11px] cht-font-bold cht-text-[#3e57da] cht-bg-[#3e57da]/10 cht-px-2 cht-py-0.5 cht-rounded-md !cht-font-body">
                        {course.progressPercent}%
                      </span>
                    ) : (
                      <span className="cht-text-[11px] cht-font-medium cht-text-slate-400 cht-bg-slate-50 cht-px-2 cht-py-0.5 cht-rounded-md !cht-font-body">
                        Up Next
                      </span>
                    )}
                  </div>

                  <h4 className="cht-text-sm sm:cht-text-[15px] cht-font-bold cht-text-slate-900 !cht-font-body cht-mt-2.5 cht-line-clamp-2 group-hover:cht-text-[#3e57da] cht-transition-colors">
                    {course.title}
                  </h4>

                  <p className="cht-text-xs cht-text-slate-400 !cht-font-body cht-mt-1">
                    {course.totalLessons} lessons • {course.duration || "Self-paced"}
                  </p>

                  {/* Course Mini Progress */}
                  <div className="cht-w-full cht-h-1.5 cht-bg-slate-100 cht-rounded-full cht-overflow-hidden cht-mt-3">
                    <div
                      className={`cht-h-full cht-rounded-full ${
                        course.isCompleted ? "cht-bg-emerald-500" : "cht-bg-[#3e57da]"
                      }`}
                      style={{ width: `${course.progressPercent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Active Course Banner when a course is chosen */}
          {activeCourse && (
            <div className="cht-space-y-4">
              <div className="cht-bg-white cht-border cht-border-indigo-100 cht-rounded-[24px] cht-p-5 sm:cht-p-6 cht-shadow-[0_4px_20px_rgba(62,87,218,0.06)] cht-relative cht-overflow-hidden">
                {/* Subtle accent indicator bar on the left */}
                <div className="cht-absolute cht-left-0 cht-top-0 cht-bottom-0 cht-w-1.5 cht-bg-[#3e57da]"></div>

                <div className="cht-flex cht-flex-col lg:cht-flex-row lg:cht-items-center lg:cht-justify-between cht-gap-5">
                  {/* Left course info */}
                  <div className="cht-space-y-2 cht-flex-1 cht-min-w-0">
                    <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
                      <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-[#3e57da] cht-text-white cht-text-[10px] !cht-font-body cht-font-bold cht-uppercase cht-tracking-wider cht-shadow-sm">
                        <Layers size={11} />
                        Current Course
                      </span>
                      <span className="cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-indigo-50 cht-text-[#3e57da] cht-text-xs !cht-font-body cht-font-bold cht-border cht-border-indigo-100/80">
                        Phase 0{activeCourse.phaseNumber} of {coursesInTrack.length}
                      </span>
                      {activeCourse.isCompleted && (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-emerald-50 cht-text-emerald-700 cht-text-xs !cht-font-body cht-font-bold cht-border cht-border-emerald-200/60">
                          <CheckCircle2 size={13} />
                          Course Completed
                        </span>
                      )}
                    </div>

                    <h3 className="!cht-font-body cht-text-lg sm:cht-text-xl !cht-font-extrabold cht-text-slate-900 cht-tracking-tight">
                      {activeCourse.title}
                    </h3>

                    {activeCourse.description && (
                      <p className="!cht-font-body cht-text-xs sm:cht-text-[13px] cht-text-slate-600 !cht-leading-relaxed cht-line-clamp-2 cht-max-w-3xl">
                        {activeCourse.description.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}
                      </p>
                    )}
                  </div>

                  {/* Right Progress Capsule */}
                  <div className="cht-flex cht-items-center cht-gap-4 cht-shrink-0 cht-bg-slate-50/90 cht-border cht-border-slate-200/80 cht-rounded-2xl cht-p-3.5 sm:cht-px-5 sm:cht-py-3">
                    <div className="cht-space-y-1.5 cht-w-full sm:cht-w-auto">
                      <div className="cht-flex cht-items-center cht-justify-between cht-gap-4">
                        <span className="!cht-font-body cht-text-xs cht-font-bold cht-text-slate-700">
                          {activeCourse.completedCount} of {activeCourse.totalLessons} Lessons
                        </span>
                        <span className={`!cht-font-body cht-text-xs cht-font-black ${
                          activeCourse.isCompleted ? "cht-text-emerald-600" : "cht-text-[#3e57da]"
                        }`}>
                          {activeCourse.progressPercent}% Completed
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="cht-w-full sm:cht-w-44 cht-h-2 cht-bg-slate-200/80 cht-rounded-full cht-overflow-hidden">
                        <div
                          className={`cht-h-full cht-rounded-full cht-transition-all cht-duration-500 ${
                            activeCourse.isCompleted
                              ? "cht-bg-emerald-500"
                              : "cht-bg-gradient-to-r cht-from-[#3e57da] cht-to-indigo-500"
                          }`}
                          style={{ width: `${activeCourse.progressPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course-Level GitHub Workspace Banner for Active Course in Track */}
              <CourseGitHubWorkspaceBanner
                courseId={activeCourse.id}
                courseTitle={activeCourse.title}
                templateRepo={activeCourse.autograder_template_repo}
                track={contents?.title}
                hasAutogradedExercises={(activeCourse.lessons || []).some(
                  (l: any) => l.itemType === "exercise" && l.requires_autograder
                )}
              />
            </div>
          )}
        </div>
      )}

      {/* Modules & Classwork List (Clean Accordion Module Cards) */}
      <div className="cht-space-y-6">
        {modules.map((module: any, mIdx: number) => {
          const modId = module.id || mIdx;
          const isModExpanded = expandedModules.has(modId);
          const lessonsList = module.lessons || [];
          const exercisesList = module.exercises || [];
          const allItems = [
            ...lessonsList.map((l: any) => ({
              ...l,
              itemType: "lesson",
              moduleId: modId,
              courseId: module.courseId || contents?.id,
              courseTemplateRepo: module.courseTemplateRepo || contents?.autograder_template_repo,
            })),
            ...exercisesList.map((e: any) => ({
              ...e,
              itemType: "exercise",
              moduleId: modId,
              courseId: module.courseId || contents?.id,
              courseTemplateRepo: module.courseTemplateRepo || contents?.autograder_template_repo,
            }))
          ];

          const completedInModule = allItems.filter((it: any) => completedLessons.has(String(it.id))).length;
          const isModuleFullyCompleted = allItems.length > 0 && completedInModule === allItems.length;

          return (
            <div
              key={modId}
              className="cht-bg-white cht-rounded-[22px] cht-border cht-border-slate-200/80 cht-shadow-[0_2px_14px_rgba(0,0,0,0.02)] cht-overflow-hidden cht-transition-all"
            >
              {/* Module Header Bar */}
              <div
                onClick={() => toggleModule(modId)}
                className="cht-p-5 sm:cht-p-6 cht-flex cht-items-center cht-justify-between cht-cursor-pointer hover:cht-bg-slate-50/70 cht-transition-colors cht-group select-none"
              >
                <div className="cht-flex cht-items-center cht-gap-3.5 cht-min-w-0">
                  <span className="cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-indigo-50 cht-text-indigo-600 cht-text-[11px] cht-font-bold cht-tracking-wider cht-uppercase !cht-font-body cht-shrink-0">
                    Module {String(mIdx + 1).padStart(2, "0")}
                  </span>
                  <div className="cht-min-w-0">
                    {selectedCourseId === "all" && module.courseTitle && (
                      <span className="cht-block cht-text-[10px] cht-font-bold cht-text-slate-400 cht-uppercase cht-tracking-wider !cht-font-body cht-truncate">
                        {module.courseTitle}
                      </span>
                    )}
                    <h2 className="cht-text-base sm:cht-text-lg cht-font-bold cht-text-slate-900 !cht-font-body cht-tracking-tight group-hover:cht-text-[#3e57da] cht-transition-colors cht-truncate">
                      {module.title}
                    </h2>
                  </div>
                </div>

                <div className="cht-flex cht-items-center cht-gap-3 cht-shrink-0">
                  {/* Module Completion Badge */}
                  <span className={`cht-text-xs cht-font-semibold cht-px-2.5 cht-py-1 cht-rounded-full !cht-font-body cht-flex cht-items-center cht-gap-1.5 ${
                    isModuleFullyCompleted
                      ? "cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200/80"
                      : "cht-bg-slate-100/80 cht-text-slate-600"
                  }`}>
                    {isModuleFullyCompleted && <CheckCircle2 size={13} className="cht-text-emerald-600" />}
                    {completedInModule}/{allItems.length} Completed
                  </span>

                  <div className="cht-w-8 cht-h-8 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-text-slate-400 group-hover:cht-text-slate-700 cht-transition-transform">
                    <motion.div animate={{ rotate: isModExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                      <ChevronDown size={18} strokeWidth={2.2} />
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Module Items List */}
              <AnimatePresence initial={false}>
                {isModExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    className="cht-overflow-hidden cht-border-t cht-border-slate-100"
                  >
                    {allItems.length === 0 ? (
                      <div className="cht-py-8 cht-text-center cht-text-xs cht-text-slate-400 !cht-font-body">
                        No materials or assignments in this module yet.
                      </div>
                    ) : (
                      <div className="cht-divide-y cht-divide-slate-100">
                        {allItems.map((item: any) => {
                          const isExpanded = expandedLessonId === item.id;
                          const isCompleted = completedLessons.has(String(item.id));
                          const isVideo = !!item.video_url;
                          const isExercise = item.itemType === "exercise";

                          return (
                            <div key={item.id} className="cht-transition-colors">
                              
                              {/* --- COLLAPSED ROW --- */}
                              {!isExpanded ? (
                                <div
                                  onClick={() => toggleLesson(item.id)}
                                  className={`cht-flex cht-items-center cht-justify-between cht-p-4 sm:cht-px-6 hover:cht-bg-slate-50/90 cht-transition-colors cht-cursor-pointer cht-group ${
                                    isCompleted ? "cht-bg-slate-50/30" : ""
                                  }`}
                                >
                                  {/* Left: Quick Completion Checkbox + Icon + Title */}
                                  <div className="cht-flex cht-items-center cht-gap-3.5 sm:cht-gap-4 cht-min-w-0">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleCompletion(item.id, e);
                                      }}
                                      title={isCompleted ? "Mark incomplete" : "Mark completed"}
                                      className={`cht-w-6 cht-h-6 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-transition-all cht-shrink-0 ${
                                        isCompleted
                                          ? "cht-bg-emerald-500 cht-text-white cht-shadow-sm hover:cht-bg-emerald-600 hover:cht-scale-105"
                                          : "cht-border-2 cht-border-slate-300 hover:cht-border-[#3e57da] cht-bg-transparent hover:cht-scale-105"
                                      }`}
                                    >
                                      {isCompleted && <Check size={13} strokeWidth={3} />}
                                    </button>

                                    {/* Icon Badge */}
                                    <div className={`cht-w-9 cht-h-9 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-shrink-0 ${
                                      isVideo
                                        ? "cht-bg-rose-50 cht-text-rose-600 cht-border cht-border-rose-100/80 cht-shadow-sm"
                                        : isExercise
                                        ? "cht-bg-amber-50 cht-text-amber-600 cht-border cht-border-amber-100/80 cht-shadow-sm"
                                        : "cht-bg-sky-50 cht-text-sky-600 cht-border cht-border-sky-100/80 cht-shadow-sm"
                                    }`}>
                                      {isVideo ? (
                                        <Play size={14} className="cht-fill-rose-600" />
                                      ) : isExercise ? (
                                        <ClipboardList size={16} strokeWidth={2} />
                                      ) : (
                                        <BookOpen size={16} strokeWidth={2} />
                                      )}
                                    </div>

                                    {/* Item Title & Info */}
                                    <div className="cht-min-w-0">
                                      <p className={`cht-text-sm sm:cht-text-[15px] cht-font-semibold !cht-font-body cht-truncate cht-transition-colors ${
                                        isCompleted
                                          ? "cht-text-slate-600 group-hover:cht-text-[#3e57da]"
                                          : "cht-text-slate-900 group-hover:cht-text-[#3e57da]"
                                      }`}>
                                        {item.title}
                                      </p>
                                      <div className="cht-flex cht-items-center cht-flex-wrap cht-gap-2 cht-mt-0.5">
                                        {isVideo ? (
                                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-rose-600 cht-bg-rose-50 cht-border cht-border-rose-200/60 cht-px-2 cht-py-0.5 cht-rounded-md !cht-font-body">
                                            <Play size={7} className="cht-fill-rose-500" />
                                            Video Lesson
                                          </span>
                                        ) : isExercise ? (
                                          <>
                                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-amber-700 cht-bg-amber-50 cht-border cht-border-amber-200/60 cht-px-2 cht-py-0.5 cht-rounded-md !cht-font-body">
                                              <ClipboardList size={9} />
                                              Assignment
                                            </span>
                                            {item.requires_autograder && item.autograder_module_name && (
                                              <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-mono cht-font-bold cht-text-[#3e57da] cht-bg-indigo-50 cht-border cht-border-indigo-200/70 cht-px-2 cht-py-0.5 cht-rounded-md">
                                                <FileText size={9} />
                                                {item.autograder_module_name}
                                              </span>
                                            )}
                                          </>
                                        ) : (
                                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-sky-700 cht-bg-sky-50 cht-border cht-border-sky-200/60 cht-px-2 cht-py-0.5 cht-rounded-md !cht-font-body">
                                            <BookOpen size={9} />
                                            Reading & Notes
                                          </span>
                                        )}
                                        {isVideo && item.content && (
                                          <span className="cht-text-[11px] cht-text-slate-400 !cht-font-body">
                                            • Includes notes
                                          </span>
                                        )}
                                        {item.duration && (
                                          <>
                                            <span className="cht-text-slate-300">•</span>
                                            <span className="cht-text-[11px] cht-text-slate-400 !cht-font-body cht-flex cht-items-center cht-gap-1">
                                              <Clock size={11} />
                                              {item.duration}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Right Side Status & Actions */}
                                  <div className="cht-flex cht-items-center cht-gap-3 cht-shrink-0">
                                    {isVideo && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveVideoModal({
                                            url: item.video_url,
                                            title: item.title,
                                            duration: item.duration,
                                            lessonId: item.id,
                                          });
                                        }}
                                        title="Watch video lesson"
                                        className="cht-hidden sm:cht-inline-flex cht-items-center cht-gap-1.5 cht-text-[11px] cht-font-bold cht-text-rose-600 cht-bg-rose-50 hover:cht-bg-rose-100 cht-border cht-border-rose-200/70 cht-px-2.5 cht-py-1 cht-rounded-full !cht-font-body cht-transition-colors"
                                      >
                                        <Play size={10} className="cht-fill-rose-500" />
                                        Watch Video
                                      </button>
                                    )}

                                    {isCompleted ? (
                                      <span className="cht-hidden sm:cht-inline-flex cht-items-center cht-gap-1 cht-text-[11px] cht-font-bold cht-text-emerald-700 cht-bg-emerald-50 cht-px-2.5 cht-py-1 cht-rounded-full !cht-font-body">
                                        <Check size={12} strokeWidth={2.5} />
                                        Completed
                                      </span>
                                    ) : (
                                      <span className="cht-hidden sm:cht-inline-flex cht-text-[11px] cht-font-semibold cht-text-indigo-600 cht-bg-indigo-50 cht-px-2.5 cht-py-1 cht-rounded-full !cht-font-body group-hover:cht-bg-indigo-100 cht-transition-colors">
                                        Start Lesson
                                      </span>
                                    )}

                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openMaterialDetail(item);
                                      }}
                                      title="Open full lesson view"
                                      className="cht-w-8 cht-h-8 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-text-slate-400 hover:cht-text-slate-800 hover:cht-bg-slate-100 cht-transition-colors"
                                    >
                                      <ChevronRight size={16} strokeWidth={2} />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                /* Modern Expanded Card */
                                <div className="cht-m-3 sm:cht-m-4 cht-bg-slate-50/70 cht-border cht-border-slate-200/90 cht-rounded-2xl cht-overflow-hidden cht-shadow-sm cht-transition-all">
                                  {/* Header Bar */}
                                  <div
                                    onClick={() => toggleLesson(item.id)}
                                    className="cht-bg-white cht-p-4 sm:cht-px-5 cht-border-b cht-border-slate-200/70 cht-flex cht-items-center cht-justify-between cht-cursor-pointer"
                                  >
                                    <div className="cht-flex cht-items-center cht-gap-3.5 cht-min-w-0">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleCompletion(item.id, e);
                                        }}
                                        title={isCompleted ? "Mark incomplete" : "Mark completed"}
                                        className={`cht-w-6 cht-h-6 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-transition-all cht-shrink-0 ${
                                          isCompleted
                                            ? "cht-bg-emerald-500 cht-text-white"
                                            : "cht-border-2 cht-border-slate-300 hover:cht-border-[#3e57da] cht-bg-transparent"
                                        }`}
                                      >
                                        {isCompleted && <Check size={13} strokeWidth={3} />}
                                      </button>

                                      <div className="cht-flex cht-items-center cht-gap-2.5 cht-min-w-0 cht-flex-wrap">
                                        <span className="cht-text-[15px] cht-font-bold cht-text-slate-900 !cht-font-body cht-truncate">
                                          {item.title}
                                        </span>

                                        {isVideo ? (
                                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-rose-600 cht-bg-rose-50 cht-border cht-border-rose-200/70 cht-px-2 cht-py-0.5 cht-rounded-full !cht-font-body cht-shrink-0">
                                            <Play size={7} className="cht-fill-rose-500" />
                                            Video Included
                                          </span>
                                        ) : isExercise ? (
                                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-amber-700 cht-bg-amber-50 cht-border cht-border-amber-200/70 cht-px-2 cht-py-0.5 cht-rounded-full !cht-font-body cht-shrink-0">
                                            <ClipboardList size={9} />
                                            Assignment
                                          </span>
                                        ) : (
                                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-sky-700 cht-bg-sky-50 cht-border cht-border-sky-200/70 cht-px-2 cht-py-0.5 cht-rounded-full !cht-font-body cht-shrink-0">
                                            <BookOpen size={9} />
                                            Reading Guide
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="cht-flex cht-items-center cht-gap-3 cht-shrink-0">
                                      {isCompleted ? (
                                        <span className="cht-text-[11px] cht-font-bold cht-text-emerald-700 cht-bg-emerald-50 cht-px-2.5 cht-py-1 cht-rounded-full !cht-font-body">
                                          Completed
                                        </span>
                                      ) : (
                                        <span className="cht-text-[11px] cht-font-semibold cht-text-indigo-600 cht-bg-indigo-50 cht-px-2.5 cht-py-1 cht-rounded-full !cht-font-body">
                                          In Progress
                                        </span>
                                      )}
                                      <ChevronUp size={18} className="cht-text-slate-400" />
                                    </div>
                                  </div>

                                  {/* Content Area */}
                                  <div className="cht-p-5 sm:cht-p-6 cht-space-y-4">
                                    {(item.content || item.exercise_task) && (
                                      <div className="cht-space-y-2">
                                        {isVideo && (
                                          <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-font-bold cht-text-slate-500 cht-uppercase cht-tracking-wider !cht-font-body">
                                            <FileText size={13} className="cht-text-slate-400" />
                                            <span>Lesson Notes & Overview</span>
                                          </div>
                                        )}
                                        <div 
                                          key={`modal-body-${item?.id || item?.title}`}
                                          className="cht-text-slate-700 cht-text-sm cht-leading-relaxed !cht-font-body [&_h1]:cht-text-lg [&_h1]:cht-font-bold [&_h1]:!cht-font-body [&_h1]:cht-text-slate-900 [&_h2]:cht-text-base [&_h2]:cht-font-bold [&_h2]:!cht-font-body [&_h2]:cht-text-slate-900 [&_h3]:cht-text-sm [&_h3]:cht-font-bold [&_h3]:!cht-font-body [&_h3]:cht-text-slate-900 [&_p]:cht-my-2.5 [&_ul]:cht-list-disc [&_ul]:cht-pl-5 [&_ul]:cht-my-2 [&_li]:cht-my-1.5 [&_strong]:cht-font-semibold [&_strong]:cht-text-slate-900 [&_code]:cht-bg-slate-100 [&_code]:cht-text-slate-900 [&_code]:cht-px-1.5 [&_code]:cht-py-0.5 [&_code]:cht-rounded-md [&_pre]:cht-bg-slate-900 [&_pre]:cht-text-slate-100 [&_pre]:cht-p-4 [&_pre]:cht-rounded-xl">
                                          {parse(item.content || item.exercise_task || "")}
                                        </div>
                                      </div>
                                    )}

                                    {isExercise && (
                                      <ExerciseSubmission
                                        exercise={item}
                                        courseId={item.courseId || contents?.id}
                                        moduleId={item.moduleId}
                                        track={contents?.type === "track" ? contents?.title : undefined}
                                        courseTemplateRepo={item.courseTemplateRepo || module.courseTemplateRepo || contents?.autograder_template_repo}
                                      />
                                    )}

                                    {isVideo && (
                                      <VideoAttachmentShowcase
                                        videoUrl={item.video_url}
                                        title={item.title || "Video Lesson"}
                                        duration={item.duration}
                                        onOpenModal={() =>
                                          setActiveVideoModal({
                                            url: item.video_url,
                                            title: item.title || "Video Lesson",
                                            duration: item.duration,
                                            lessonId: item.id,
                                          })
                                        }
                                      />
                                    )}

                                    {/* Action Footer */}
                                    <div className="cht-pt-4 cht-border-t cht-border-slate-200/70 cht-flex cht-items-center cht-justify-between cht-gap-4">
                                      <button
                                        type="button"
                                        onClick={() => openMaterialDetail(item)}
                                        className="cht-text-[#3e57da] hover:cht-text-indigo-700 cht-text-xs sm:cht-text-sm cht-font-semibold hover:cht-underline cht-inline-flex cht-items-center cht-gap-1.5 cht-transition-colors !cht-font-body"
                                      >
                                        Open full lesson view
                                        <ChevronRight size={14} />
                                      </button>

                                      <div className="cht-flex cht-items-center cht-gap-2.5">
                                        <Button
                                          variant={isCompleted ? "outline" : "primary"}
                                          size="sm"
                                          className={`cht-rounded-xl cht-text-[11px] !cht-font-body ${
                                            isCompleted
                                              ? "cht-border-emerald-500 cht-text-emerald-700 hover:cht-bg-emerald-50"
                                              : ""
                                          }`}
                                          onClick={(e: any) => handleToggleCompletion(item.id, e)}
                                          leftIcon={isCompleted ? <Check size={14} /> : <CheckCircle2 size={14} />}
                                        >
                                          {isCompleted ? "Completed" : "Mark Complete"}
                                        </Button>

                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="cht-rounded-xl cht-text-[11px] cht-text-slate-600 hover:cht-text-slate-900 !cht-font-body"
                                          onClick={() => handleNextLesson(item.id)}
                                          rightIcon={<ChevronRight size={14} />}
                                        >
                                          Next
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Quick Notes Slide-Over */}
      <AnimatePresence>
        {showNotesDrawer && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className="cht-fixed cht-right-6 cht-top-24 cht-bottom-12 cht-w-80 sm:cht-w-96 cht-bg-white cht-rounded-[24px] cht-border cht-border-gray-200 cht-shadow-2xl cht-p-6 cht-flex cht-flex-col cht-z-50"
          >
            <div className="cht-flex cht-items-center cht-justify-between cht-pb-4 cht-border-b cht-border-gray-100">
              <div className="cht-flex cht-items-center cht-gap-2.5">
                <StickyNote size={18} className="cht-text-[#3e57da]" />
                <h4 className="cht-text-sm cht-font-bold cht-text-slate-900">Personal Notes</h4>
              </div>
              <button
                onClick={() => setShowNotesDrawer(false)}
                className="cht-text-slate-400 hover:cht-text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Take notes while studying..."
              className="cht-flex-1 cht-w-full cht-bg-slate-50 cht-rounded-xl cht-p-4 cht-text-xs cht-font-medium cht-text-slate-700 cht-border cht-border-slate-100 cht-outline-none cht-resize-none cht-mt-4 focus:cht-bg-white focus:cht-border-[#3e57da] cht-transition-all"
            />

            <div className="cht-pt-3 cht-flex cht-items-center cht-justify-between cht-text-[10px] cht-text-slate-400">
              <span>Auto-saved locally</span>
              <span>{notes.length} characters</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Modal Overlay */}
      {activeVideoModal && (
        <VideoModal
          isOpen={!!activeVideoModal}
          videoUrl={activeVideoModal.url}
          title={activeVideoModal.title}
          duration={activeVideoModal.duration}
          isCompleted={completedLessons.has(String(activeVideoModal.lessonId))}
          onToggleComplete={() => {
            if (activeVideoModal.lessonId) {
              handleToggleCompletion(activeVideoModal.lessonId);
            }
          }}
          onClose={() => setActiveVideoModal(null)}
        />
      )}

    </div>
  );
};

export default CourseLearning;
